import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { settingsApi } from "./api";
import {
  IMAGE_JOB_QUEUE_MAX_POLL_ATTEMPTS,
  IMAGE_JOB_QUEUE_HISTORY_MS,
  IMAGE_JOB_QUEUE_POLL_CONCURRENCY,
  IMAGE_JOB_QUEUE_POLL_INTERVAL_MS,
  imageJobQueueSource,
  isImageJobQueueItemActive,
} from "./image-job-queue";
import { SkeletonGrid } from "./Skeleton";
import type { ManualImagePublicJob } from "./types";
import { ImageUsageMeter } from "./ImageUsageMeter";
import "./image-job-queue.css";

const STATUS_LABELS: Record<ManualImagePublicJob["status"], string> = {
  queued: "排队中",
  claimed: "生成中",
  generated: "已生成",
  delivered: "已完成",
  failed: "失败",
  outcome_unknown: "结果待确认",
};

const SOURCE_LABELS: Record<ManualImagePublicJob["source"], string> = {
  dialogue: "对话生图",
  command: "指令生图",
  manual: "手动生图",
};

function timeLabel(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function failureDetail(job: ManualImagePublicJob): string {
  if (!job.error) return "服务端没有提供可展示的失败原因。";
  if (job.error.code === "timeout") return "任务处理超时（错误代码：timeout）";
  if (job.error.code === "image_preflight_quote_failed") return "费用预检暂时失败，尚未提交生成，请稍后再试。（错误代码：image_preflight_quote_failed）";
  if (job.error.code === "image_preflight_prepare_failed") return "生成请求准备失败，尚未提交生成。（错误代码：image_preflight_prepare_failed）";
  return `错误代码：${job.error.code}`;
}

function mergeFirstPage(current: readonly ManualImagePublicJob[], latest: readonly ManualImagePublicJob[]): ManualImagePublicJob[] {
  const latestIds = new Set(latest.map((job) => job.id));
  const cutoff = Date.now() - IMAGE_JOB_QUEUE_HISTORY_MS;
  return [...latest, ...current.filter((job) => !latestIds.has(job.id))]
    .filter((job) => Date.parse(job.created_at) >= cutoff);
}

export function ImageJobQueueSettings({ active, headerActionsTarget, onFill }: {
  active: boolean;
  headerActionsTarget?: HTMLElement | null;
  onFill: (prompts: { positive_prompt: string; negative_prompt: string }) => void;
}) {
  const [jobs, setJobs] = useState<ManualImagePublicJob[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [pollPaused, setPollPaused] = useState(false);
  const [expandedFailure, setExpandedFailure] = useState<string | null>(null);
  const [clearStage, setClearStage] = useState<"idle" | "confirm">("idle");
  const [clearing, setClearing] = useState(false);
  const [clearNotice, setClearNotice] = useState("");
  const [usageRevision, setUsageRevision] = useState(0);
  const [actionJobId, setActionJobId] = useState<string | null>(null);
  const jobsRef = useRef(jobs);
  const nextCursorRef = useRef(nextCursor);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);
  const pollAttemptsRef = useRef(0);
  const activeRef = useRef(active);
  activeRef.current = active;

  function commit(nextJobs: ManualImagePublicJob[], cursor: string | null): void {
    jobsRef.current = nextJobs;
    nextCursorRef.current = cursor;
    setJobs(nextJobs);
    setNextCursor(cursor);
  }

  function stopRequest(): void {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
    abortRef.current?.abort();
    abortRef.current = null;
  }

  function schedulePoll(): void {
    if (!activeRef.current || !mountedRef.current || timerRef.current !== null || !jobsRef.current.some(isImageJobQueueItemActive)) return;
    if (pollAttemptsRef.current >= IMAGE_JOB_QUEUE_MAX_POLL_ATTEMPTS) {
      setPollPaused(true);
      return;
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void loadFirstPage(true);
    }, IMAGE_JOB_QUEUE_POLL_INTERVAL_MS);
  }

  async function loadFirstPage(polling = false): Promise<void> {
    if (!mountedRef.current) return;
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    if (polling) pollAttemptsRef.current += 1;
    else setLoading(jobsRef.current.length === 0);
    try {
      const activeJobs = polling ? jobsRef.current.filter(isImageJobQueueItemActive) : [];
      const refreshedActive: ManualImagePublicJob[] = [];
      for (let index = 0; index < activeJobs.length; index += IMAGE_JOB_QUEUE_POLL_CONCURRENCY) {
        const batch = activeJobs.slice(index, index + IMAGE_JOB_QUEUE_POLL_CONCURRENCY);
        const results = await Promise.allSettled(batch.map((job) => settingsApi.loadManualImageJob(job.id, controller.signal)));
        if (controller.signal.aborted) return;
        for (const result of results) if (result.status === "fulfilled") refreshedActive.push(result.value);
      }
      const page = await settingsApi.loadImageJobQueue(undefined, controller.signal);
      if (!mountedRef.current || controller.signal.aborted) return;
      const refreshedById = new Map(refreshedActive.map((job) => [job.id, job]));
      const refreshedCurrent = jobsRef.current.map((job) => refreshedById.get(job.id) ?? job);
      const nextJobs = polling ? mergeFirstPage(refreshedCurrent, page.jobs) : page.jobs;
      commit(nextJobs, polling && jobsRef.current.length > page.jobs.length ? nextCursorRef.current : page.next_cursor);
      setError("");
      setPollPaused(false);
    } catch {
      if (mountedRef.current && !controller.signal.aborted) setError(polling ? "暂时无法刷新任务状态，将继续重试。" : "无法读取生图队列，请稍后重试。");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (mountedRef.current && activeRef.current) {
        setLoading(false);
        schedulePoll();
      }
    }
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor || loadingMore) return;
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
    const requestedCursor = nextCursor;
    const controller = new AbortController();
    abortRef.current = controller;
    setLoadingMore(true);
    setError("");
    try {
      const page = await settingsApi.loadImageJobQueue(requestedCursor, controller.signal);
      if (!mountedRef.current || controller.signal.aborted) return;
      const known = new Set(jobsRef.current.map((job) => job.id));
      commit([...jobsRef.current, ...page.jobs.filter((job) => !known.has(job.id))], page.next_cursor);
    } catch {
      if (mountedRef.current && !controller.signal.aborted) setError("无法加载更多生图任务，请稍后重试。");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      if (mountedRef.current && activeRef.current) {
        setLoadingMore(false);
        schedulePoll();
      }
    }
  }

  async function clearHistory(): Promise<void> {
    if (clearing) return;
    if (clearStage === "idle") {
      setClearStage("confirm");
      setClearNotice("");
      return;
    }
    setClearing(true);
    setError("");
    setClearNotice("");
    try {
      const result = await settingsApi.clearManualImageHistory(crypto.randomUUID());
      const retained = jobsRef.current.filter((job) => !["delivered", "failed"].includes(job.status));
      commit(retained, null);
      setExpandedFailure(null);
      setClearStage("idle");
      setClearNotice(result.cleared > 0 ? `已清空 ${result.cleared} 条已完成或失败的历史任务。` : "没有可清空的已完成或失败任务。");
      setUsageRevision((value) => value + 1);
    } catch {
      setError("无法清空生图历史，请稍后重试。");
    } finally {
      setClearing(false);
    }
  }

  function replaceJob(nextJob: ManualImagePublicJob): void {
    commit(jobsRef.current.map((job) => job.id === nextJob.id ? nextJob : job), nextCursorRef.current);
  }

  async function cancelJob(job: ManualImagePublicJob): Promise<void> {
    if (job.status !== "queued" || actionJobId !== null) return;
    setActionJobId(job.id);
    setError("");
    try {
      replaceJob(await settingsApi.cancelManualImageJob(job.id));
    } catch {
      setError("无法取消这项生图任务。任务已保留，请稍后重试。");
    } finally {
      if (mountedRef.current) setActionJobId(null);
    }
  }

  async function deleteJob(job: ManualImagePublicJob): Promise<void> {
    if (job.status !== "delivered" || actionJobId !== null) return;
    setActionJobId(job.id);
    setError("");
    try {
      await settingsApi.deleteManualImageJob(job.id);
      commit(jobsRef.current.filter((item) => item.id !== job.id), nextCursorRef.current);
    } catch {
      setError("无法删除这条生图记录。记录已保留，请稍后重试。");
    } finally {
      if (mountedRef.current) setActionJobId(null);
    }
  }

  // 生命周期只由 active 控制；请求函数通过 ref 读取最新列表，并在 cleanup 中统一中止。
  // biome-ignore lint/correctness/useExhaustiveDependencies: active 是隐藏面板启停网络活动的唯一开关。
  useEffect(() => {
    if (!active) {
      mountedRef.current = false;
      stopRequest();
      return;
    }
    mountedRef.current = true;
    void loadFirstPage();
    return () => {
      mountedRef.current = false;
      stopRequest();
    };
  }, [active]);

  const headerActions = <div className="image-job-queue-heading-actions">
        <ImageUsageMeter active={active} refreshKey={usageRevision} />
        {clearStage === "confirm" ? <><button className="secondary outlined" type="button" disabled={clearing} onClick={() => setClearStage("idle")}>取消</button><button className="danger" type="button" disabled={clearing} onClick={() => void clearHistory()}>{clearing ? "清空中…" : "确认清空"}</button></> : <button className="secondary outlined" type="button" disabled={clearing} onClick={() => void clearHistory()}>清空记录</button>}
        <button className="secondary outlined" type="button" disabled={loading || clearing} onClick={() => { pollAttemptsRef.current = 0; stopRequest(); void loadFirstPage(); }}>刷新</button>
      </div>;
  return <div className="image-job-queue">
    {active ? headerActionsTarget ? createPortal(headerActions, headerActionsTarget) : headerActions : null}
    {loading && jobs.length === 0 ? <SkeletonGrid items={5} variant="row" label="正在读取生图队列" /> : <>
    {error ? <p className="action-error image-job-queue-error" role="alert">{error}</p> : null}
    {clearNotice ? <p className="action-success image-job-queue-clear-notice" role="status">{clearNotice}</p> : null}
    {pollPaused ? <output className="image-job-queue-notice">自动刷新已暂停。任务仍会在服务端继续处理；点击“刷新”获取最新状态。</output> : null}
    {jobs.length === 0 ? <p className="panel-state">最近 7 天还没有生图任务。</p> : <div className="image-job-queue-list">
      {jobs.map((job) => <article className="image-job-row" key={job.id}>
        <div className="image-job-row-main">
          <div className="image-job-row-meta"><span>{SOURCE_LABELS[imageJobQueueSource(job)]}</span><time dateTime={job.created_at}>{timeLabel(job.created_at)}</time></div>
          <p>{job.metadata.positive_prompt}</p>
        </div>
        <span className={`image-job-status is-${job.status}`}>{STATUS_LABELS[job.status]}</span>
        <div className="image-job-actions">
          <button className="secondary outlined" type="button" onClick={() => onFill({ positive_prompt: job.metadata.positive_prompt, negative_prompt: job.metadata.negative_prompt })}>填充</button>
          {job.status === "failed" ? <button className="secondary outlined" type="button" aria-expanded={expandedFailure === job.id} onClick={() => setExpandedFailure((current) => current === job.id ? null : job.id)}>详情</button> : null}
          {job.status === "queued" ? <button className="secondary outlined" type="button" disabled={actionJobId !== null} onClick={() => void cancelJob(job)}>{actionJobId === job.id ? "取消中…" : "取消"}</button> : null}
          {job.status === "claimed" || job.status === "generated" || job.status === "outcome_unknown" ? <button className="secondary outlined" type="button" disabled>取消</button> : null}
          {job.status === "delivered" ? <button className="secondary outlined" type="button" disabled={actionJobId !== null} onClick={() => void deleteJob(job)}>{actionJobId === job.id ? "删除中…" : "删除"}</button> : null}
        </div>
        {job.status === "failed" && expandedFailure === job.id ? <output className="image-job-failure">{failureDetail(job)}</output> : null}
      </article>)}
    </div>}
    {jobs.length > 0 ? <div className="image-job-queue-page-state">{loadingMore ? <SkeletonGrid items={2} variant="row" label="正在加载更多生图任务" /> : nextCursor ? <button className="secondary outlined" type="button" onClick={() => void loadMore()}>加载更多</button> : "已经到底了"}</div> : null}
    </>}
  </div>;
}
